import {
  ChangeDetectionStrategy,
  Component,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  ICartDraftDeclinableProductItem,
  ICartDraftSimpleProductItem,
} from '@core/models/cart.model';
import {
  IContentBuilderFieldColor,
  IContentBuilderFieldImage,
} from '@core/models/content-builder-fields.model';
import { DoodProductModel } from '@core/models/product.model';
import { ContentBuilderColorPipe } from '@core/pipes/content-builder-color.pipe';
import { ProductService } from '@core/services/product/product.service';
import { ProductsService } from '@core/services/products/products.service';
import { TranslateModule } from '@ngx-translate/core';
import { KioskProductDetailsBlockComponent } from '@shared/blocks/kiosk/kiosk-product-details-block/kiosk-product-details-block.component';
import { Step } from '@shared/local-store/stepper/stepper.type';
import {
  BehaviorSubject,
  debounceTime,
  delay,
  filter,
  map,
  share,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';

/** Payload for the parent to run `CartService.addOrUpdateCartItem$`. */
export type KioskProductAddShellAddToCartPayload = {
  cartDraft: ICartDraftSimpleProductItem | ICartDraftDeclinableProductItem;
  validity: { isValid: boolean };
  quantity: number;
  product: DoodProductModel | null;
};

@Component({
  selector: 'app-kiosk-product-add-shell',
  templateUrl: './kiosk-product-add-shell.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [KioskProductDetailsBlockComponent, TranslateModule, ContentBuilderColorPipe],
})
export class KioskProductAddShellComponent implements OnInit, OnDestroy {
  readonly productId = input.required<string>();
  readonly shopId = input.required<string>();

  readonly backgroundColor = input<IContentBuilderFieldColor>();
  readonly backgroundImage = input<IContentBuilderFieldImage>();
  readonly secondaryColor = input<IContentBuilderFieldColor>();
  readonly textColor = input<IContentBuilderFieldColor>();
  readonly buttonBackgroundColor = input<IContentBuilderFieldColor>();
  readonly buttonTextColor = input<IContentBuilderFieldColor>();
  readonly unavailableButtonBackground = input<IContentBuilderFieldColor>();
  readonly unavailableButtonText = input<IContentBuilderFieldColor>();
  readonly cardColor = input<IContentBuilderFieldColor>();
  readonly compositionBackgroundColor = input<IContentBuilderFieldColor>();

  /** Parent performs cart API and post-success navigation / modal close. */
  readonly addToCartRequest = output<KioskProductAddShellAddToCartPayload>();
  /** Stepper back at root: parent runs navigation (page) or closes modal. */
  readonly backRequest = output<void>();

  private readonly _destroyerRef = new Subject<boolean>();

  protected readonly addToCart$ = new BehaviorSubject<
    | {
        cartDraft: ICartDraftSimpleProductItem | ICartDraftDeclinableProductItem;
        validity: {
          isValid: boolean;
        };
      }
    | undefined
  >(undefined);

  protected readonly selectedStep$ = new Subject<Step | undefined>();

  private addToCartRequested$ = this.addToCart$.pipe(
    filter(data => !!data),
    map(data => data as NonNullable<typeof data>),
  );

  private readonly isProductStepperStable$ = this.selectedStep$.pipe(
    debounceTime(50),
    take(1),
    switchMap(() => this.addToCart$),
    map(addToCartTriggerEarly => {
      if (addToCartTriggerEarly) {
        return 'earlyAddToCart' as const;
      }
      return 'stable' as const;
    }),
    take(1),
    startWith('unstable' as const),
    share(),
  );

  protected readonly isProductStepperStable = toSignal(this.isProductStepperStable$);

  protected readonly product = signal<DoodProductModel | null>(null);

  protected readonly quantity = signal(1);

  constructor(
    private readonly productService: ProductService,
    private readonly productsService: ProductsService,
  ) {
    this.isProductStepperStable$
      .pipe(
        filter(isProductStepperStable => isProductStepperStable !== 'unstable'),
        take(1),
        switchMap(isProductStepperStable => {
          if (isProductStepperStable === 'earlyAddToCart') {
            return this.addToCartRequested$.pipe(delay(1300));
          }

          return this.addToCartRequested$;
        }),
        takeUntilDestroyed(),
      )
      .subscribe(addToCartData => this.emitAddToCart(addToCartData));
  }

  ngOnInit(): void {
    const shopId = this.shopId();
    const productId = this.productId();

    this.productsService
      .selectByShopAndProduct$(shopId, productId)
      .pipe(
        take(1),
        map((product: DoodProductModel | undefined) =>
          product === undefined ? null : { ...product, quantity: 0 },
        ),
        tap(product => product && this.productService.setProductToStore(product)),
        tap(product => {
          this.product.set(product);
        }),
        takeUntil(this._destroyerRef),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this._destroyerRef.next(true);
    this._destroyerRef.complete();
  }

  onBackRequest(): void {
    this.backRequest.emit();
  }

  private emitAddToCart({
    cartDraft,
    validity,
  }: {
    cartDraft: ICartDraftSimpleProductItem | ICartDraftDeclinableProductItem;
    validity: { isValid: boolean };
  }): void {
    if (!validity.isValid) {
      return;
    }
    this.addToCartRequest.emit({
      cartDraft,
      validity,
      quantity: this.quantity(),
      product: this.product(),
    });
  }
}
